import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Order } from "./use-orders";

export const usePartnerOrders = (partnerId?: string) => {
  return useQuery({
    queryKey: ['partner-orders', partnerId],
    queryFn: async () => {
      if (!partnerId) throw new Error("Partner ID is required");
      
      console.log("🔍 Fetching orders for partner:", partnerId);
      
      // Use the database function to get partner orders with proper relationship handling
      const { data, error } = await supabase.rpc('get_partner_orders', {
        partner_id_param: partnerId
      });

      if (error) {
        console.error("❌ Error fetching partner orders:", error);
        throw error;
      }
      
      console.log(`📊 Fetched ${data?.length || 0} orders for partner ${partnerId}`);
      
      // Transform the data to match the expected Order interface
      const transformedOrders: Order[] = data?.map((row: any) => ({
        id: row.order_id,
        status: row.order_status,
        order_type: row.order_type,
        total_cameras: row.total_cameras,
        total_images: row.total_images,
        created_at: row.created_at,
        updated_at: row.updated_at,
        allocated_device_ids: row.allocated_device_ids,
        fulfillment_status: row.fulfillment_status,
        fulfillment_notes: row.fulfillment_notes,
        tracking_number: row.tracking_number,
        shipped_at: row.shipped_at,
        estimated_delivery_date: row.estimated_delivery_date,
        customer_user_id: row.customer_user_id,
        organization_id: row.organization_id,
        order_data: row.order_data,
        sale_id: row.sale_id,
        sale: {
          id: row.sale_id,
          deal_id: row.deal_id,
          total_amount: row.sale_total_amount,
          status: row.sale_status,
          currency: row.sale_currency,
          payment_due_date: row.sale_payment_due_date,
          invoice_number: row.sale_invoice_number,
          invoice_sent_at: row.sale_invoice_sent_at,
          deal: {
            id: row.deal_id,
            title: row.deal_title,
            contact_id: row.deal_contact_id,
            contact: {
              first_name: row.contact_first_name,
              last_name: row.contact_last_name,
              email: row.contact_email,
              external_owner_id: row.contact_external_owner_id
            }
          }
        }
      })) || [];
      
      console.log(`✅ Successfully transformed ${transformedOrders.length} orders for partner ${partnerId}:`, 
        transformedOrders.map(order => ({
          id: order.id.toString().slice(0, 8),
          status: order.status,
          orderType: order.order_type,
          dealTitle: order.sale?.deal?.title
        }))
      );
      
      return transformedOrders;
    },
    enabled: !!partnerId,
  });
};